// src/lib/managers/conversation/contract.ts

/**
 * Lecture tolérante du contrat v1 (F-1.17, plan §6.2 règle 5) : le parc mobile reste figé des
 * semaines alors que l'Api-Ai évolue. Une réponse que cette version ne sait pas traiter — outil
 * inconnu, `toolCall` absent, arguments illisibles — donne une réponse texte, jamais une exception.
 */

import { buildTextResponse } from "./textResponse";
import { ComponentData } from "./types/conversation";

/** Réponse à une demande que cette version de l'application ne sait pas traiter. */
export const UNSUPPORTED_REQUEST_MESSAGE =
    "Je ne peux pas encore faire cela dans cette version de l'application.";

/** En échec, `reason` part dans les logs : nom d'outil et nature du problème, jamais de contenu. */
export type ReadToolCallResult =
    | { ok: true; name: string; args: Record<string, unknown> }
    | { ok: false; reason: string };

/**
 * Lit le `toolCall` v1 d'une réponse de l'Api-Ai, sans jamais lever. Un outil n'est reconnu que s'il
 * est une clé PROPRE de `knownTools` (« constructor » ou « toString », hérités, ne le sont pas), et
 * ses arguments doivent être un objet JSON.
 */
export function readToolCall(
    response: unknown,
    knownTools: object,
): ReadToolCallResult {
    const toolCall = (response as { toolCall?: unknown } | null | undefined)
        ?.toolCall;
    const toolFunction = (
        toolCall as
            | { function?: { name?: unknown; arguments?: unknown } }
            | null
            | undefined
    )?.function;
    const name = toolFunction?.name;

    if (typeof name !== "string" || name === "") {
        return { ok: false, reason: "toolCall absent ou sans nom d'outil" };
    }
    if (!Object.prototype.hasOwnProperty.call(knownTools, name)) {
        return { ok: false, reason: `outil inconnu « ${name} »` };
    }

    let args: unknown;
    try {
        args = JSON.parse(String(toolFunction?.arguments));
    } catch {
        return { ok: false, reason: `arguments illisibles pour « ${name} »` };
    }
    if (typeof args !== "object" || args === null || Array.isArray(args)) {
        return {
            ok: false,
            reason: `arguments de « ${name} » qui ne sont pas un objet`,
        };
    }

    return { ok: true, name, args: args as Record<string, unknown> };
}

/** Réponse texte à une demande non prise en charge : un tour normal, qui entre dans l'historique. */
export const buildUnsupportedResponse = (): ComponentData =>
    buildTextResponse(UNSUPPORTED_REQUEST_MESSAGE);
